/**
 * Other players' movement, smoothed: each one is a short history of timed
 * samples, read back a little in the past so there is always a sample on
 * either side to interpolate between.
 *
 * Samples are timed by the room (`PlayerRow.at`, when it heard each
 * position) and moved onto this client's clock by `ClockSync`: so a burst of
 * late messages does not make a player sprint, and a repeated position in
 * the room's snapshots adds nothing.
 *
 * Pure: no DOM, no three.js.
 */

import type { PlayerMode } from '../config.js';
import type { FallStart } from '../core/character/fall.js';

export interface Sample {
  /** Local clock, ms. */
  t: number;
  lat: number;
  lon: number;
  alt: number;
  heading: number;
  mode: PlayerMode;
  /** Down, and how they fell. */
  fall?: FallStart;
}

export interface TrackPose {
  lat: number;
  lon: number;
  alt: number;
  heading: number;
  mode: PlayerMode;
  fall?: FallStart;
  /** Ground speed, m/s, and vertical speed, m/s up. */
  speed: number;
  climb: number;
}

const KEEP = 8;
/** A gap longer than this between samples is a pause, not slow movement, ms. */
const RESUME_MS = 1500;
const RESUME_GAP_MS = 250;
/** Past its last sample a player coasts on for this long, then stops, ms. */
const COAST_MS = 500;

function metresPerDegree(lat: number): { lat: number; lon: number } {
  return { lat: 111_320, lon: 111_320 * Math.cos((lat * Math.PI) / 180) };
}

/** The shortest turn from a to b, degrees. */
function turn(a: number, b: number): number {
  return ((((b - a) % 360) + 540) % 360) - 180;
}

export class Track {
  private readonly samples: Sample[] = [];

  add(s: Sample): void {
    const last = this.samples[this.samples.length - 1];
    if (last !== undefined && s.t <= last.t) return;
    // After a pause the player was standing where they last were until just
    // before this: say so, or they would glide there over the whole pause.
    if (last !== undefined && s.t - last.t > RESUME_MS) this.samples.push({ ...last, t: s.t - RESUME_GAP_MS });
    this.samples.push(s);
    if (this.samples.length > KEEP) this.samples.shift();
  }

  get last(): Sample | undefined {
    return this.samples[this.samples.length - 1];
  }

  /**
   * How far behind to read: a little more than the usual gap between samples,
   * so the next one has nearly always arrived. Four a second reads about half
   * a second back; one a second, about 1.4 s.
   */
  delay(): number {
    const n = this.samples.length;
    if (n < 2) return 600;
    const gaps: number[] = [];
    for (let i = 1; i < n; i++) gaps.push(this.samples[i].t - this.samples[i - 1].t);
    gaps.sort((a, b) => a - b);
    const typical = gaps[Math.floor(gaps.length / 2)];
    return Math.max(350, Math.min(2500, typical * 1.2 + 250));
  }

  /** Where the player was at local time `time`, or undefined with no samples. */
  at(time: number): TrackPose | undefined {
    const s = this.samples;
    if (s.length === 0) return undefined;
    let i = s.length - 1;
    while (i > 0 && s[i - 1].t > time) i--;
    // Before the first sample: hold it.
    if (time <= s[0].t || s.length === 1) return { ...s[0], speed: 0, climb: 0 };
    const b = s[i], a = s[i - 1];
    const span = Math.max(1, b.t - a.t);
    const m = metresPerDegree(a.lat);
    const dist = Math.hypot((b.lat - a.lat) * m.lat, (b.lon - a.lon) * m.lon);
    // A jump across the map is a teleport: no gliding over the continent.
    const teleport = dist / (span / 1000) > 400;
    const speed = teleport ? 0 : dist / (span / 1000);
    const climb = teleport ? 0 : (b.alt - a.alt) / (span / 1000);
    if (time >= b.t) {
      // Past the newest: coast on briefly at the last velocity, then hold.
      const ahead = Math.min(time - b.t, COAST_MS) / span;
      const k = teleport ? 0 : ahead;
      return {
        lat: b.lat + (b.lat - a.lat) * k, lon: b.lon + (b.lon - a.lon) * k, alt: b.alt + (b.alt - a.alt) * k,
        heading: b.heading, mode: b.mode, fall: b.fall, speed: time - b.t > COAST_MS ? 0 : speed, climb,
      };
    }
    const k = teleport ? 1 : (time - a.t) / span;
    return {
      lat: a.lat + (b.lat - a.lat) * k, lon: a.lon + (b.lon - a.lon) * k, alt: a.alt + (b.alt - a.alt) * k,
      heading: (a.heading + turn(a.heading, b.heading) * k + 360) % 360,
      mode: k < 0.5 ? a.mode : b.mode, fall: k < 0.5 ? a.fall : b.fall, speed, climb,
    };
  }
}

/**
 * The offset from the room's clock to this client's, from the room's
 * timestamps and when they arrived. The smallest recent offset is the one
 * that travelled fastest, and the truest; a longer delivery only adds to it.
 */
export class ClockSync {
  private readonly recent: number[] = [];
  offset: number | undefined;

  note(roomNow: number, localNow: number): void {
    this.recent.push(localNow - roomNow);
    if (this.recent.length > 40) this.recent.shift();
    this.offset = Math.min(...this.recent);
  }

  toLocal(roomTime: number): number {
    return roomTime + (this.offset ?? 0);
  }
}
